import React, { useEffect, useState, lazy } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Bell, Star, Calendar as CalendarIcon, ChevronRight, Scissors, Users } from 'lucide-react';
import { useStore, getUpcoming, handleImgError } from '../lib/store';
import { Logo, LogoMark } from '../components/Logo';
export function Home() {
  const navigate = useNavigate();
  const {
    user,
    onboarded,
    appointments,
    notifications,
    barbers,
    services,
    promotions
  } = useStore();
  const [showSplash, setShowSplash] = useState(true);
  useEffect(() => {
    if (!onboarded) navigate('/onboarding');else if (!user.loggedIn && !user.isGuest) navigate('/login');
  }, [onboarded, user, navigate]);
  useEffect(() => {
    const timer = setTimeout(() => setShowSplash(false), 2000);
    return () => clearTimeout(timer);
  }, []);
  if (showSplash) {
    return <div className="min-h-screen bg-dark flex flex-col items-center justify-center relative overflow-hidden" data-id="element-848">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1503951914875-452162b0f3f1?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-20" data-id="element-849" />
        <div className="absolute inset-0 bg-gradient-to-b from-dark/50 to-dark" data-id="element-850" />
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.8
      }} className="z-10 flex flex-col items-center px-8" data-id="element-851">
          <Logo size={240} className="mb-2" data-id="element-852" />
          <p className="text-[10px] tracking-[0.3em] text-gold uppercase" data-id="element-853">
            Reservá. Pagá. Relajate.
          </p>
        </motion.div>
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1
      }} className="absolute bottom-12 flex flex-col items-center gap-4 z-10" data-id="element-854">
          <div className="w-6 h-6 border-2 border-gold border-t-transparent rounded-full animate-spin" data-id="element-855" />
          <p className="text-xs text-light-muted" data-id="element-856">Cargando experiencia...</p>
        </motion.div>
      </div>;
  }
  const upcoming = getUpcoming(appointments);
  const nextAppt = upcoming[0];
  const unreadCount = notifications.filter(n => !n.read).length;
  return <div className="px-6 pt-8 pb-6 space-y-8" data-id="element-857">
      {/* Brand row — outlined emblem */}
      <div className="flex items-center gap-2 text-gold" data-id="element-858">
        <LogoMark size={26} color="#C8A548" data-id="element-859" />
        <span className="text-sm font-semibold tracking-widest text-light" data-id="element-860">
          BARBER<span className="text-gold" data-id="element-861">OS</span>
        </span>
      </div>

      <header className="flex justify-between items-center" data-id="element-862">
        <div data-id="element-863">
          <h2 className="text-2xl font-semibold text-light" data-id="element-864">
            Hola, {user.name} 👋
          </h2>
          <p className="text-sm text-light-muted mt-1" data-id="element-865">
            ¿Listo para tu próximo corte?
          </p>
        </div>
        <Link to="/notificaciones" className="w-10 h-10 rounded-full bg-dark-card border border-white/5 flex items-center justify-center text-light relative" data-id="element-866">
          <Bell size={20} data-id="element-867" />
          {unreadCount > 0 && <span className="absolute top-2 right-2 w-2 h-2 bg-gold rounded-full" data-id="element-868" />}
        </Link>
      </header>

      {/* Trust indicators */}
      <section aria-label="Indicadores de confianza" className="grid grid-cols-3 gap-3" data-id="element-869">
        {[{
        icon: <Star size={18} fill="currentColor" data-id="element-870" />,
        value: '4.9/5',
        label: 'en reseñas'
      }, {
        icon: <Users size={18} data-id="element-871" />,
        value: '+2.500',
        label: 'clientes atendidos'
      }, {
        icon: <Scissors size={18} data-id="element-872" />,
        value: String(barbers.length),
        label: 'barberos profesionales'
      }].map(item => <div key={item.label} className="bg-dark-card rounded-2xl border border-white/5 p-3 flex flex-col items-center text-center gap-1" data-id="element-873">
            <span className="text-gold" data-id="element-874">{item.icon}</span>
            <span className="text-base font-semibold text-light leading-none" data-id="element-875">
              {item.value}
            </span>
            <span className="text-[10px] text-light-muted leading-tight" data-id="element-876">
              {item.label}
            </span>
          </div>)}
      </section>

      {/* Banner */}
      <div className="bg-dark-card rounded-3xl p-6 border border-white/5 relative overflow-hidden" data-id="element-877">
        <div className="relative z-10 w-2/3" data-id="element-878">
          <h3 className="text-lg font-medium text-light mb-1" data-id="element-879">
            Reservá en menos de 1 minuto.
          </h3>
          <Link to="/reservar" className="inline-block mt-4 bg-gold text-dark px-5 py-2.5 rounded-xl text-xs font-semibold hover:bg-gold-hover transition-colors" data-id="element-880">
            Reservar ahora
          </Link>
        </div>
        <div className="absolute right-0 bottom-0 w-32 h-32 opacity-20 translate-x-4 translate-y-4 text-gold" data-id="element-881">
          <CalendarIcon className="w-full h-full" data-id="element-882" />
        </div>
      </div>

      {/* Next Appointment */}
      {nextAppt && <section data-id="element-883">
          <h3 className="text-lg font-medium text-light mb-4" data-id="element-884">Próximo turno</h3>
          <div className="bg-dark-card rounded-2xl p-4 border border-white/5 flex items-center gap-4" data-id="element-885">
            <div className="w-14 h-14 bg-gold/10 rounded-xl flex flex-col items-center justify-center text-gold border border-gold/20" data-id="element-886">
              <span className="text-[10px] font-medium uppercase" data-id="element-887">
                {new Date(nextAppt.date).toLocaleDateString('es-AR', {
              month: 'short'
            })}
              </span>
              <span className="text-lg font-bold leading-none" data-id="element-888">
                {nextAppt.date.split('-')[2]}
              </span>
            </div>
            <div className="flex-1" data-id="element-889">
              <p className="text-sm font-medium text-light" data-id="element-890">
                {nextAppt.time} hs
              </p>
              <p className="text-xs text-light-muted" data-id="element-891">
                Con {nextAppt.barberName}
              </p>
            </div>
            <Link to="/mis-turnos" className="text-xs text-gold font-medium px-3 py-1.5 border border-gold/30 rounded-lg hover:bg-gold/10 transition-colors" data-id="element-892">
              Ver detalle
            </Link>
          </div>
        </section>}

      {/* Promos */}
      <section data-id="element-893">
        <h3 className="text-lg font-medium text-light mb-4" data-id="element-894">Promociones</h3>
        <div className="grid grid-cols-2 gap-3" data-id="element-895">
          {promotions.map(p => <div key={p.id} className="rounded-2xl p-4 min-h-[92px] flex flex-col justify-center relative overflow-hidden" style={{
          backgroundColor: p.color
        }} data-id="element-896">
              <h4 className="text-lg font-bold text-white mb-1 leading-tight" data-id="element-897">
                {p.title}
              </h4>
              <p className="text-xs text-white/90 leading-snug" data-id="element-898">{p.subtitle}</p>
            </div>)}
        </div>
      </section>

      {/* Barbers */}
      <section data-id="element-899">
        <div className="flex justify-between items-end mb-4" data-id="element-900">
          <h3 className="text-lg font-medium text-light" data-id="element-901">
            Barberos destacados
          </h3>
          <Link to="/barberos" className="text-xs text-gold font-medium" data-id="element-902">
            Ver todos
          </Link>
        </div>
        <div className="grid grid-cols-3 gap-3" data-id="element-903">
          {barbers.map(barber => <Link key={barber.id} to={`/barberos/${barber.id}`} className="text-left group bg-dark-card p-2 rounded-2xl border border-white/5" data-id="element-904">
              <img src={barber.image} alt={barber.name} loading="lazy" onError={handleImgError} className="w-full aspect-square rounded-xl object-cover object-[center_20%] mb-2 bg-white/5" data-id="element-905" />
              <h4 className="font-medium text-xs text-light truncate" data-id="element-906">
                {barber.name}
              </h4>
              <p className="text-[10px] text-light-muted truncate mb-1" data-id="element-907">
                {barber.specialties[0]}
              </p>
              <div className="flex items-center gap-1 text-[10px] text-gold" data-id="element-908">
                <Star size={10} fill="currentColor" data-id="element-909" />
                <span data-id="element-910">{barber.rating}</span>
              </div>
            </Link>)}
        </div>
      </section>

      {/* Services */}
      <section data-id="element-911">
        <div className="flex justify-between items-end mb-4" data-id="element-912">
          <h3 className="text-lg font-medium text-light" data-id="element-913">Servicios</h3>
          <Link to="/servicios" className="text-xs text-gold font-medium" data-id="element-914">
            Ver todos
          </Link>
        </div>
        <div className="grid grid-cols-4 gap-3" data-id="element-915">
          {services.slice(0, 3).map(service => <Link key={service.id} to="/reservar" className="flex flex-col items-center text-center gap-2" data-id="element-916">
              <div className="w-16 h-16 rounded-2xl bg-dark-card border border-white/5 flex items-center justify-center text-gold hover:bg-gold/10 transition-colors" data-id="element-917">
                <Scissors size={24} data-id="element-918" />
              </div>
              <div data-id="element-919">
                <p className="text-xs font-medium text-light" data-id="element-920">{service.name}</p>
              </div>
            </Link>)}
          <Link to="/servicios" className="flex flex-col items-center text-center gap-2" data-id="element-921">
            <div className="w-16 h-16 rounded-2xl bg-dark-card border border-white/5 flex items-center justify-center text-light-muted hover:text-light transition-colors" data-id="element-922">
              <ChevronRight size={24} data-id="element-923" />
            </div>
            <div data-id="element-924">
              <p className="text-xs font-medium text-light" data-id="element-925">Más</p>
            </div>
          </Link>
        </div>
      </section>
    </div>;
}